import React, { useState } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, increment } from 'firebase/firestore';

const AdminDashboard = ({ allUsers, setAllUsers }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [creditToAdd, setCreditToAdd] = useState('');
  const [isAddingCredits, setIsAddingCredits] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleAddCredits = async () => {
    if (!selectedUserId || !creditToAdd || creditToAdd <= 0) {
      setErrorMessage('Please select a user and enter a valid credit amount');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    setIsAddingCredits(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const userRef = doc(db, 'users', selectedUserId);
      const creditsToAdd = parseInt(creditToAdd);
      
      await updateDoc(userRef, {
        credits: increment(creditsToAdd)
      });

      // Update local state
      setAllUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === selectedUserId
            ? { ...user, credits: (user.credits || 0) + creditsToAdd }
            : user
        )
      );

      const selectedUser = allUsers.find(u => u.id === selectedUserId);
      setSuccessMessage(`✅ Successfully added ${creditsToAdd} credits to ${selectedUser?.email}`);
      
      // Reset form
      setSelectedUserId('');
      setCreditToAdd('');
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error adding credits:', error);
      setErrorMessage(`❌ Failed to add credits: ${error.message}`);
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsAddingCredits(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Admin Dashboard</h2>
        
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
        )}
        
        {errorMessage && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-800 font-medium">{errorMessage}</p>
          </div>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
            <p className="text-sm text-gray-600 mb-1">Total Users</p>
            <p className="text-3xl font-bold text-indigo-600">{allUsers.length}</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <p className="text-sm text-gray-600 mb-1">Total Invoices Processed</p>
            <p className="text-3xl font-bold text-green-600">
              {allUsers.reduce((sum, u) => sum + (u.invoicesProcessed || 0), 0)}
            </p>
          </div>
          <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <p className="text-sm text-gray-600 mb-1">Total Credits in System</p>
            <p className="text-3xl font-bold text-amber-600">
              {allUsers.reduce((sum, u) => sum + (u.credits || 0), 0)}
            </p>
          </div>
        </div>

        {/* Add Credits to User */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Credits to User</h3>
          <div className="grid grid-cols-3 gap-4">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={isAddingCredits}
            >
              <option value="">Select User</option>
              {allUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.email} ({u.credits || 0} credits)
                </option>
              ))}
            </select>
            <input
              type="number"
              value={creditToAdd}
              onChange={(e) => setCreditToAdd(e.target.value)}
              placeholder="Credits to add"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min="1"
              disabled={isAddingCredits}
            />
            <button
              onClick={handleAddCredits}
              disabled={isAddingCredits || !selectedUserId || !creditToAdd}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAddingCredits ? 'Adding...' : 'Add Credits'}
            </button>
          </div>
        </div>

        {/* User List */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">All Users</h3>
          <div className="space-y-3">
            {allUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No users found
              </div>
            ) : (
              allUsers.map(u => (
                <div key={u.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{u.email}</p>
                    <p className="text-sm text-gray-500">{u.name || 'N/A'}</p>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-gray-500">Credits</p>
                      <p className="font-bold text-indigo-600">{u.credits || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-500">Invoices Processed</p>
                      <p className="font-bold text-green-600">{u.invoicesProcessed || 0}</p>
                    </div>
                    {u.isAdmin && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        Admin
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;